<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import LuxButton from '@/Components/Public/LuxButton.vue'

// The private-or-shared block (competitor teardown item 7): one real, priced
// boat from our catalog against the published price of a seat on a shared
// day boat to the same islands. The numbers arrive computed from the live
// price (PrivateVsShared concern) so the block can never contradict the
// boat's own page; the qualitative rows are copy.
const { t } = useI18n()
const props = defineProps({
  comparison:    { type: Object, default: null },  // see PrivateVsShared#comparison_for
  charters_path: { type: String, default: '/charters' },
  plan_path:     { type: String, default: '/plan' }
})

const rows = ['departure', 'route', 'stops', 'aboard', 'pickup', 'lunch', 'children', 'price_basis']
const boatHref = computed(() => props.comparison ? `${props.charters_path}/${props.comparison.boat.id}` : props.charters_path)
const seatLine = computed(() => {
  const s = props.comparison.shared
  return s.child
    ? t('public.compare.seat_price', { price: s.adult, child: s.child })
    : t('public.compare.seat_price_adult', { price: s.adult })
})
const verdict = computed(() => {
  const c = props.comparison
  if (c.break_even) return t('public.compare.break_even', { n: c.break_even })
  if (c.at_full.diff) return t('public.compare.never_cheaper', { n: c.at_full.guests, diff: c.at_full.diff })
  return ''
})
</script>

<template>
  <section v-if="comparison" id="private-vs-shared" class="cmp" :aria-label="t('public.compare.title')">
    <div class="cmp__head">
      <p class="cmp__eyebrow">{{ t('public.compare.eyebrow') }}</p>
      <h2 class="cmp__title">{{ t('public.compare.title') }}</h2>
      <p class="cmp__sub">{{ t('public.compare.sub') }}</p>
    </div>

    <div class="cmp__cols">
      <div class="cmp__col cmp__col--shared">
        <p class="cmp__colhead">{{ t('public.compare.col_shared', { kind: comparison.shared.kind }) }}</p>
        <p class="cmp__price fw-value">{{ seatLine }}</p>
        <p class="cmp__note">{{ t('public.compare.seat_source', { year: comparison.shared.year }) }}</p>
      </div>
      <div class="cmp__col cmp__col--private">
        <p class="cmp__colhead">{{ t('public.compare.col_private') }}</p>
        <p class="cmp__price fw-value">{{ t('public.compare.boat_price', { price: comparison.boat.price, n: comparison.boat.covers }) }}</p>
        <p class="cmp__note"><a :href="boatHref" class="cmp__boat">{{ comparison.boat.title }}</a></p>
      </div>
    </div>

    <div class="cmp__ladder" role="table" :aria-label="t('public.compare.guests')">
      <div class="cmp__lrow cmp__lrow--head" role="row">
        <span role="columnheader">{{ t('public.compare.guests') }}</span>
        <span role="columnheader">{{ t('public.compare.per_person_private') }}</span>
        <span role="columnheader">{{ t('public.compare.per_person_shared') }}</span>
      </div>
      <div v-for="step in comparison.ladder" :key="step.guests" class="cmp__lrow" role="row" :class="{ 'cmp__lrow--even': comparison.break_even && step.guests >= comparison.break_even }">
        <span role="cell" class="fw-value">{{ step.guests }}</span>
        <span role="cell" class="fw-value">{{ step.private_pp }}</span>
        <span role="cell" class="fw-value">{{ comparison.shared.adult }}</span>
      </div>
    </div>
    <p v-if="verdict" class="cmp__verdict">{{ verdict }}</p>

    <div class="cmp__incl">
      <div>
        <p class="cmp__ih">{{ t('public.compare.includes') }}</p>
        <ul class="cmp__list"><li v-for="(x, i) in comparison.boat.inclusions" :key="i">{{ x }}</li></ul>
      </div>
      <div v-if="comparison.boat.not_included?.length">
        <p class="cmp__ih">{{ t('public.compare.not_included') }}</p>
        <ul class="cmp__list cmp__list--plus"><li v-for="(x, i) in comparison.boat.not_included" :key="i">{{ x }}</li></ul>
      </div>
    </div>

    <dl class="cmp__rows">
      <div v-for="r in rows" :key="r" class="cmp__row">
        <dt>{{ t(`public.compare.rows.${r}.label`) }}</dt>
        <dd class="cmp__shared">{{ t(`public.compare.rows.${r}.shared`) }}</dd>
        <dd class="cmp__private">{{ t(`public.compare.rows.${r}.private`) }}</dd>
      </div>
    </dl>

    <div class="cmp__ctas">
      <LuxButton :href="boatHref" variant="solid" tone="light" glyph="arrow">{{ t('public.compare.cta_boat') }}</LuxButton>
      <LuxButton :href="plan_path" variant="line" tone="light">{{ t('public.compare.cta_plan') }}</LuxButton>
    </div>
  </section>
</template>

<style scoped>
.cmp { display: grid; gap: 1.5rem; padding: clamp(1.5rem, 3vw, 2.25rem); background: var(--surface); border: 1px solid var(--line); border-radius: 1.25rem; }
.cmp__head { display: grid; gap: 0.4rem; max-width: 60ch; }
.cmp__eyebrow { margin: 0; font-size: 0.72rem; letter-spacing: var(--eyebrow-tracking, 0.12em); text-transform: uppercase; font-weight: 700; color: var(--accent); }
.cmp__title { margin: 0; font-family: var(--font-display, var(--display)); font-weight: 500; font-size: clamp(1.7rem, 3vw, 2.4rem); line-height: 1.05; color: var(--ink); text-wrap: balance; }
.cmp__sub { margin: 0; color: var(--inkSoft); line-height: 1.5; }

.cmp__cols { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.cmp__col { padding: 1.1rem 1.25rem; border-radius: 1rem; border: 1px solid var(--line); background: var(--bg); display: grid; gap: 0.35rem; align-content: start; }
.cmp__col--private { border-color: color-mix(in oklch, var(--gold, var(--warn)) 55%, var(--line)); background: color-mix(in oklch, var(--accentSoft) 55%, var(--bg)); }
.cmp__colhead { margin: 0; font-size: 0.75rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; color: var(--muted); }
.cmp__price { margin: 0; font-size: 1.15rem; color: var(--ink); }
.cmp__note { margin: 0; font-size: 0.85rem; color: var(--inkSoft); line-height: 1.45; }
.cmp__boat { color: var(--azure, var(--second)); font-weight: 700; text-decoration: none; }
.cmp__boat:hover { text-decoration: underline; }

.cmp__ladder { display: grid; gap: 0; border: 1px solid var(--line); border-radius: 0.9rem; overflow: hidden; font-variant-numeric: tabular-nums; }
.cmp__lrow { display: grid; grid-template-columns: 5rem 1fr 1fr; gap: 0.5rem; padding: 0.6rem 1rem; border-top: 1px solid var(--line); }
.cmp__lrow:first-child { border-top: 0; }
.cmp__lrow--head { background: var(--bg); font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); font-weight: 700; }
.cmp__lrow--even { background: color-mix(in oklch, var(--okSoft, var(--accentSoft)) 60%, transparent); }
.cmp__verdict { margin: -0.5rem 0 0; font-family: var(--font-display, var(--display)); font-size: 1.2rem; line-height: 1.3; color: var(--ink); max-width: 60ch; }

.cmp__incl { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.cmp__ih { margin: 0 0 0.4rem; font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; color: var(--muted); }
.cmp__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.3rem; font-size: 0.92rem; color: var(--ink); }
.cmp__list li { position: relative; padding-left: 1.3rem; line-height: 1.4; }
.cmp__list li::before { content: "✓"; position: absolute; left: 0; color: var(--ok); font-weight: 700; }
.cmp__list--plus li::before { content: "+"; color: var(--accent); }

.cmp__rows { margin: 0; display: grid; gap: 0; border-top: 1px solid var(--line); }
.cmp__row { display: grid; grid-template-columns: 9rem 1fr 1fr; gap: 0.75rem; padding: 0.7rem 0; border-bottom: 1px solid var(--line); }
.cmp__row dt { font-size: 0.8rem; font-weight: 700; color: var(--muted); letter-spacing: 0.04em; }
.cmp__row dd { margin: 0; font-size: 0.92rem; line-height: 1.45; }
.cmp__shared { color: var(--inkSoft); }
.cmp__private { color: var(--ink); }
.cmp__ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; }

@media (max-width: 48rem) {
  .cmp__cols, .cmp__incl { grid-template-columns: 1fr; }
  .cmp__row { grid-template-columns: 1fr; gap: 0.2rem; }
  .cmp__row dd::before { font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); font-weight: 700; margin-right: 0.4rem; }
  .cmp__shared::before { content: "·"; }
  .cmp__private::before { content: "·"; }
  .cmp__lrow { grid-template-columns: 3.5rem 1fr 1fr; padding: 0.55rem 0.7rem; font-size: 0.9rem; }
}
</style>
